// شناسه‌های ارائه‌دهنده نزد بیمه‌گر در تب ارائه‌دهنده SCR-STAFF-921 (US-IAM-088؛ AP-INT-10): شماره نظام، کد ملی یا کد فیدا و
// موبایل ثبت‌شده نزد بیمه‌گر، با **پیش‌نمایش قالب خروجی** (ماما «*»، پزشک اتباع `FDA` و پسوند «ات» — BR-IAM-081). تغییر موبایل
// ثبت‌شده یا شناسه هویتی، اعتبارنامه بیمه‌گر را نامعتبر می‌کند و پزشک باید دوباره وصل شود (BR-IAM-082).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ProviderInsurerIdentifierView, type SetProviderInsurerIdentifierRequest } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ErrorState,
  JalaliDate,
  KeyValueList,
  LoadingState,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { iamKeys } from './common';

/** بیمه‌گر دارای اتصال رسمی (فاز ۳). */
const INSURER = 'TAMIN';

const identifierSchema = z.object({
  licenseNumber: z.string().trim().min(1, { error: 'common:validation.required' }).max(32, { error: 'common:validation.too_long' }),
  nationalId: z.string().trim().max(10, { error: 'common:validation.too_long' }),
  fidaCode: z.string().trim().max(32, { error: 'common:validation.too_long' }),
  insurerMobile: z.string().trim().max(11, { error: 'common:validation.too_long' }),
  isForeignNational: z.boolean(),
});

type IdentifierForm = z.infer<typeof identifierSchema>;

export function ProviderInsurerIdentifiersSection({ providerId }: { providerId: string }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('iam.provider.manage');
  const queryClient = useQueryClient();

  const identifiers = useQuery({
    queryKey: iamKeys.insurerIdentifiers(session?.tenantId, providerId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/identity/providers/{id}/insurer-identifiers', { params: { path: { id: providerId } } }),
      ),
  });

  const current = identifiers.data?.items.find((item) => item.insurer === INSURER) ?? null;

  const { control, handleSubmit, reset, setError } = useForm<IdentifierForm>({
    resolver: zodResolver(identifierSchema),
    defaultValues: { licenseNumber: '', nationalId: '', fidaCode: '', insurerMobile: '', isForeignNational: false },
  });

  useEffect(() => {
    if (current) {
      reset({
        licenseNumber: current.licenseNumber,
        nationalId: current.nationalId ?? '',
        fidaCode: current.fidaCode ?? '',
        insurerMobile: current.insurerMobile ?? '',
        isForeignNational: current.isForeignNational,
      });
    }
  }, [current, reset]);

  const save = useMutation({
    mutationFn: (form: IdentifierForm) => {
      const body: SetProviderInsurerIdentifierRequest = {
        licenseNumber: form.licenseNumber.trim(),
        nationalId: form.nationalId.trim() || null,
        fidaCode: form.fidaCode.trim() || null,
        insurerMobile: form.insurerMobile.trim() || null,
        isForeignNational: form.isForeignNational,
      };
      return unwrap(
        tenantApi.PUT('/api/v1/identity/providers/{id}/insurer-identifiers/{insurer}', {
          params: { path: { id: providerId, insurer: INSURER } },
          body,
        }),
      );
    },
    onError: (error) =>
      applyServerErrors(error, setError, {
        fields: ['licenseNumber', 'nationalId', 'fidaCode', 'insurerMobile'],
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: iamKeys.insurerIdentifiers(session?.tenantId, providerId) }),
  });

  if (identifiers.isPending) {
    return <LoadingState rows={2} />;
  }

  if (identifiers.error) {
    return <ErrorState error={identifiers.error} onRetry={() => void identifiers.refetch()} compact />;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="form" onSubmit={handleSubmit((form) => save.mutate(form))}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">{t('iam:insurer_identifier.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('iam:insurer_identifier.subtitle')}
        </Typography>

        <TextFieldRHF
          control={control}
          name="licenseNumber"
          labelKey="iam:insurer_identifier.license_number"
          slotProps={{ htmlInput: { 'data-testid': 'insurer-identifier-license' } }}
        />
        <TextFieldRHF
          control={control}
          name="nationalId"
          labelKey="iam:insurer_identifier.national_id"
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'insurer-identifier-national-id' } }}
        />
        <CheckboxRHF control={control} name="isForeignNational" labelKey="iam:insurer_identifier.is_foreign" />
        <TextFieldRHF
          control={control}
          name="fidaCode"
          labelKey="iam:insurer_identifier.fida_code"
          helperKey="iam:insurer_identifier.fida_hint"
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'insurer-identifier-fida' } }}
        />
        <TextFieldRHF
          control={control}
          name="insurerMobile"
          labelKey="iam:insurer_identifier.insurer_mobile"
          helperKey="iam:insurer_identifier.insurer_mobile_hint"
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'insurer-identifier-mobile' } }}
        />

        {current ? <IdentifierPreview view={current} /> : null}
        {current?.invalidatedAt ? (
          <Alert severity="warning" data-testid="insurer-identifier-invalidated">
            {t('iam:insurer_identifier.invalidated')} <JalaliDate value={current.invalidatedAt} withTime />
          </Alert>
        ) : null}
        {save.data?.credentialInvalidated ? (
          <Alert severity="warning" data-testid="insurer-identifier-reconnect">
            {t('iam:insurer_identifier.reconnect_required')}
          </Alert>
        ) : null}
        {save.error ? <ErrorState error={save.error} compact /> : null}

        <Stack direction="row">
          <Button type="submit" variant="contained" disabled={!write.canWrite || save.isPending} data-testid="insurer-identifier-save">
            {t('common:actions.save')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** پیش‌نمایش قالب خروجی نزد بیمه‌گر (AC-IAM-088.3). */
function IdentifierPreview({ view }: { view: ProviderInsurerIdentifierView }) {
  const { t } = useTranslation('iam');
  return (
    <span data-testid="insurer-identifier-preview" data-doc-id={view.previewDocId}>
      <KeyValueList
        items={[
          { labelKey: 'iam:insurer_identifier.preview_doc_id', value: <bdi>{view.previewDocId}</bdi> },
          {
            labelKey: 'iam:insurer_identifier.preview_national_code',
            value: <bdi dir="ltr">{view.previewNationalCode ?? '—'}</bdi>,
          },
          {
            labelKey: 'iam:insurer_identifier.is_midwife',
            value: t(view.isMidwife ? 'iam:insurer_identifier.midwife_yes' : 'iam:insurer_identifier.midwife_no'),
          },
        ]}
      />
    </span>
  );
}
